import { useReducer } from 'react';
import { usePatientEligibility } from './usePatientEligibility';
import { message } from 'antd';
import moment from 'moment';
import { PatientInfo } from 'lib/api/firebase/apis/apis';
import { AssessmentType } from 'lib/documents/patient-assessment/patient-assessment';
import { useAssessmentAPI } from 'lib/hooks/tanstack/use-assessmentAPI';
import { useLoginLinkAPI } from 'lib/hooks/tanstack/use-loginLink';
import { useClinicianContext } from 'lib/hooks/use-resolveCurrentClinicInfo';
import { useClinicAssessmentBalance } from 'lib/hooks/tanstack/use-clinicAssessmentBalance';
import { useCurrentClinicHasPaymentMethod } from 'lib/hooks/tanstack/use-getProductPrice';
import { useErrorList } from 'lib/utils/useErrorList';
import { ProgressTableRow } from '../types';

export interface SendAssessmentState {
  isModalVisible: boolean;
  assessmentTypeToSend: AssessmentType | null;
  isSending: boolean;
  isSentComplete: boolean;
  rows: ProgressTableRow[];
  successfulCount: number;
  failedCount: number;
  skippedCount: number;
}

export type SendAssessmentAction =
  | { type: 'OPEN_MODAL'; payload: { assessmentType: AssessmentType } }
  | { type: 'CLOSE_MODAL' }
  | { type: 'START_SENDING'; payload: { initialRows: ProgressTableRow[] } }
  | {
      type: 'UPDATE_PHASE';
      payload: {
        patientId: string;
        phase: 'phase_1' | 'phase_2';
        status: 'pending' | 'completed' | 'error';
        error?: Error | null;
      };
    }
  | { type: 'FINISH_SENDING' };

const initialState: SendAssessmentState = {
  isModalVisible: false,
  assessmentTypeToSend: null,
  isSending: false,
  isSentComplete: false,
  rows: [],
  successfulCount: 0,
  failedCount: 0,
  skippedCount: 0
};

export function sendAssessmentReducer(state: SendAssessmentState, action: SendAssessmentAction): SendAssessmentState {
  switch (action.type) {
    case 'OPEN_MODAL':
      return {
        ...state,
        isModalVisible: true,
        assessmentTypeToSend: action.payload.assessmentType,
        isSending: false,
        isSentComplete: false,
        rows: [],
        successfulCount: 0,
        failedCount: 0,
        skippedCount: 0
      };
    case 'CLOSE_MODAL':
      return {
        ...state,
        isModalVisible: false
      };
    case 'START_SENDING':
      return {
        ...state,
        isSending: true,
        isSentComplete: false,
        rows: action.payload.initialRows
      };
    case 'UPDATE_PHASE':
      return {
        ...state,
        rows: state.rows.map(row => {
          if (row.id !== action.payload.patientId) {
            return row;
          }
          if (action.payload.phase === 'phase_1') {
            return {
              ...row,
              phase_1: action.payload.status as false | null | 'pending' | 'completed' | 'error',
              phase_1_error: action.payload.error ?? null
            };
          } else {
            return {
              ...row,
              phase_2: action.payload.status as false | null | 'pending' | 'completed' | 'error',
              phase_2_error: action.payload.error ?? null
            };
          }
        })
      };
    case 'FINISH_SENDING': {
      let successfulCount = 0;
      let failedCount = 0;
      let skippedCount = 0;

      state.rows.forEach(row => {
        const isSkippedAll = row.phase_1 === false && row.phase_2 === false;
        if (isSkippedAll) {
          skippedCount++;
          return;
        }

        const isP1Success = row.phase_1 === false || row.phase_1 === 'completed';
        const isP2Success = row.phase_2 === 'completed';
        const isP1Error = row.phase_1 === 'error';
        const isP2Error = row.phase_2 === 'error';

        if (isP1Success && isP2Success) {
          successfulCount++;
        } else if (isP1Error || isP2Error) {
          failedCount++;
        }
      });

      return {
        ...state,
        isSending: false,
        isSentComplete: true,
        successfulCount,
        failedCount,
        skippedCount
      };
    }
    default:
      return state;
  }
}

export function useSendAssessment(
  selectedPatients: PatientInfo[],
  onAssessmentsSent: () => void
) {
  const [state, dispatch] = useReducer(sendAssessmentReducer, initialState);

  const errorList = useErrorList();
  const clinicianContext = useClinicianContext();
  const assessmentAPI = useAssessmentAPI();
  const loginLinkAPI = useLoginLinkAPI();

  const balanceQuery = useClinicAssessmentBalance(state.assessmentTypeToSend ?? undefined);
  const hasPaymentMethodQuery = useCurrentClinicHasPaymentMethod();

  const isInfinite = balanceQuery.data?.data.isInfinite;
  const availableBalance = balanceQuery.data?.data.available ?? 0;
  const emptyBalance = availableBalance === 0 && !isInfinite;
  const hasPaymentMethod = hasPaymentMethodQuery.data?.data.hasPaymentMethod;
  const supportsOnDemand = balanceQuery.data?.data.supportsOnDemandPurchases.value;

  const isPaymentBlocked = emptyBalance && !hasPaymentMethod;
  const isBalanceEmptyAndNoOnDemand = emptyBalance && !supportsOnDemand;
  const isSendBlocked = isPaymentBlocked || isBalanceEmptyAndNoOnDemand;

  const {
    eligiblePatients,
    skippedPatients,
    confirmationRows,
    isQueriesLoading,
    analyzedCount,
    remainingCount
  } = usePatientEligibility({
    selectedPatients,
    isModalVisible: state.isModalVisible,
    assessmentTypeToSend: state.assessmentTypeToSend
  });

  const progressRows = (state.isSending || state.isSentComplete) ? state.rows : confirmationRows;

  const openModal = (type: AssessmentType) => {
    // Clear error list
    errorList.errors.forEach(e => errorList.removeError(e.id, { ignoreMissing: true }));

    dispatch({
      type: 'OPEN_MODAL',
      payload: {
        assessmentType: type
      }
    });
  };

  const closeModal = () => {
    dispatch({ type: 'CLOSE_MODAL' });
    if (state.isSentComplete) {
      onAssessmentsSent();
    }
  };

  const handleSendAssessments = async () => {
    if (!state.assessmentTypeToSend || !clinicianContext.currentClinic) {
      message.error('Missing required information to send assessments.');
      return;
    }

    dispatch({ type: 'START_SENDING', payload: { initialRows: confirmationRows } });

    const expiresAtTimestamp = moment().add(30, 'days').unix();

    // Iterate over the rows captured at the start of sending
    for (const row of confirmationRows) {
      const isSkipped = row.isSkipped;
      const isSkippedAll = row.phase_1 === false && row.phase_2 === false;

      if (isSkippedAll) {
        // All phases are skipped, do nothing for this patient!
        continue;
      }

      let currentPhase: 'create' | 'email' = 'create';

      try {
        if (!isSkipped) {
          dispatch({
            type: 'UPDATE_PHASE',
            payload: {
              patientId: row.id,
              phase: 'phase_1',
              status: 'pending'
            }
          });

          await assessmentAPI.create.mutateAsync({
            assessmentType: state.assessmentTypeToSend,
            patientId: row.id,
            clinicId: clinicianContext.currentClinic,
            importLastImpactTestOnStart: false,
            expiresAt: expiresAtTimestamp,
            skipExpiryCheck: true
          });

          await loginLinkAPI.create.mutateAsync({
            uid: row.id,
            useLimit: 10,
            expiresAt: expiresAtTimestamp
          });

          dispatch({
            type: 'UPDATE_PHASE',
            payload: {
              patientId: row.id,
              phase: 'phase_1',
              status: 'completed'
            }
          });
        }

        currentPhase = 'email';
        dispatch({
          type: 'UPDATE_PHASE',
          payload: {
            patientId: row.id,
            phase: 'phase_2',
            status: 'pending'
          }
        });

        await assessmentAPI.sendAssessmentEmail.mutateAsync({
          patientId: row.id,
          orgId: clinicianContext.currentClinic
        });

        dispatch({
          type: 'UPDATE_PHASE',
          payload: {
            patientId: row.id,
            phase: 'phase_2',
            status: 'completed'
          }
        });
      } catch (error: any) {
        console.error(
          `Failed to process assessment for patient ${row.id} in phase ${currentPhase}:`,
          error
        );
        const errorMsg = error?.message || 'An unexpected error occurred.';
        const errObj = new Error(errorMsg);

        if (currentPhase === 'create') {
          dispatch({
            type: 'UPDATE_PHASE',
            payload: {
              patientId: row.id,
              phase: 'phase_1',
              status: 'error',
              error: errObj
            }
          });
        } else {
          dispatch({
            type: 'UPDATE_PHASE',
            payload: {
              patientId: row.id,
              phase: 'phase_2',
              status: 'error',
              error: errObj
            }
          });
        }

        const patientName = row.displayName || row.email || row.id.slice(0, 6);
        errorList.addError(
          new Error(`Failed to process assessment for ${patientName} (${currentPhase === 'create' ? 'Creation' : 'Email'} Phase): ${errorMsg}`)
        );
      }
    }

    dispatch({ type: 'FINISH_SENDING' });
  };

  return {
    isModalVisible: state.isModalVisible,
    assessmentTypeToSend: state.assessmentTypeToSend,
    isSending: state.isSending,
    isSentComplete: state.isSentComplete,
    progressRows,
    successfulCount: state.successfulCount,
    failedCount: state.failedCount,
    skippedCount: state.skippedCount,
    eligiblePatients,
    skippedPatients,
    isQueriesLoading,
    analyzedCount,
    remainingCount,
    isSendBlocked,
    errorList,
    openModal,
    closeModal,
    handleSendAssessments
  };
}
